/**
 * PURE community selectors (F3-C) — testable without a DB.
 *   • `countUnread`      — bell counter (null read_at).
 *   • `groupThreadPosts` — 1-level nesting (top-level + replies per
 *     parent) for the thread view.
 */

export interface UnreadLike {
  read_at?: string | null;
}

/** Number of UNREAD notifications (null/undefined read_at). */
export function countUnread(notifications: readonly UnreadLike[]): number {
  return notifications.reduce((n, x) => (x.read_at ? n : n + 1), 0);
}

export interface PostLike {
  id: string;
  reply_to_id?: string | null;
}

export interface GroupedPosts<T extends PostLike> {
  /** Top-level posts (no reply_to). */
  topLevel: T[];
  /** Replies grouped by parent post id. */
  repliesByParent: Map<string, T[]>;
}

/**
 * Groups a thread's posts into 1 level: those without `reply_to_id` are
 * top-level; the rest hang off their parent. A reply whose parent is NOT in
 * the list (e.g. hidden/deleted) is promoted to top-level so it isn't lost.
 */
export function groupThreadPosts<T extends PostLike>(posts: readonly T[]): GroupedPosts<T> {
  const ids = new Set(posts.map((p) => p.id));
  const topLevel: T[] = [];
  const repliesByParent = new Map<string, T[]>();
  for (const p of posts) {
    const parent = p.reply_to_id;
    if (parent && ids.has(parent)) {
      const arr = repliesByParent.get(parent) ?? [];
      arr.push(p);
      repliesByParent.set(parent, arr);
    } else {
      topLevel.push(p);
    }
  }
  return { topLevel, repliesByParent };
}
